import {
  ASSISTANT_ACTION_IDS,
  ASSISTANT_VALUE_IDS,
  isAssistantActionValueCompatible,
  type AssistantActionId,
  type AssistantValueId
} from "../../shared/assistantPolicy";
import { summarizeAssistantSceneActivity } from "../../shared/assistantSceneState";
import {
  SHARED_SCENE_GUIDE_CATALOG,
  type SocraticPromptLayers
} from "../../shared/sceneGuideCatalog";
import type {
  AiDialogueMessage,
  AssistantContextSnapshot,
  AssistantLastAction,
  AssistantPhase,
  MisconceptionTag,
  SceneId,
  SceneProgress,
  SceneVisualState
} from "../types";

export const assistantActionIds = ASSISTANT_ACTION_IDS;
export const assistantValueIds = ASSISTANT_VALUE_IDS;

/** 把可序列化的学习操作收窄为动作类别，不暴露具体组件或渲染器状态。 */
export function assistantActionFromVisualPatch(patch: Partial<SceneVisualState>): AssistantLastAction {
  if (patch.placedItemIds) {
    return { actionId: "action.item.place", result: "pending", valueId: "value.placed" };
  }
  if (patch.selectedItemIds) {
    return { actionId: "action.item.select", result: "pending", valueId: "value.selected" };
  }
  return { actionId: "action.control.change", result: "pending", valueId: "value.adjusted" };
}

export interface AssistantGuideEntry {
  sceneId: SceneId;
  guideStepId: string;
  objectiveId: string;
  objectiveLabel: string;
  phase: AssistantPhase;
  ruleIds: readonly string[];
  evidenceIds: readonly string[];
  actionIds: readonly string[];
  valueIds: readonly string[];
  currentItemIds: readonly string[];
  sceneFocus: string;
  successCondition: string;
  commonMisconceptionTags: readonly MisconceptionTag[];
  layeredPrompts: SocraticPromptLayers;
  localFallback: Readonly<{ feedback: string; evidencePrompt: string }>;
}

export const assistantGuideCatalog: readonly AssistantGuideEntry[] = SHARED_SCENE_GUIDE_CATALOG.map((entry) => ({
  sceneId: entry.sceneId,
  guideStepId: entry.guideStepId,
  objectiveId: entry.objectiveId,
  objectiveLabel: entry.objectiveLabel,
  phase: entry.phase,
  ruleIds: entry.ruleIds,
  evidenceIds: entry.evidenceIds,
  actionIds: entry.allowedActionIds,
  valueIds: entry.allowedValueIds,
  currentItemIds: entry.currentItemIds,
  sceneFocus: entry.sceneFocus,
  successCondition: entry.successCondition,
  commonMisconceptionTags: entry.commonMisconceptionTags,
  layeredPrompts: entry.layeredPrompts,
  localFallback: entry.localFallback
}));

const GUIDE_BY_SCENE = new Map(assistantGuideCatalog.map((entry) => [entry.sceneId, entry]));

export function getAssistantGuide(sceneId: SceneId): AssistantGuideEntry {
  const guide = GUIDE_BY_SCENE.get(sceneId);
  if (!guide) throw new Error(`未知学习助手场景：${sceneId}`);
  return guide;
}

function boundedRecentMessages(messages: readonly AiDialogueMessage[]): AiDialogueMessage[] {
  return messages.flatMap((message) => {
    const content = message.content.trim().slice(0, message.role === "user" ? 120 : 180);
    return content ? [{ role: message.role, content }] : [];
  }).slice(-4);
}

const ASSISTANT_ACTION_RESULTS = new Set(["success", "error", "pending"]);
const MISCONCEPTION_TAGS = new Set<MisconceptionTag>([
  "evidence-missing",
  "concept-confusion",
  "causality-overstated",
  "scope-overreach",
  "expression-unclear"
]);

function sanitizeAssistantAction(
  action: AssistantLastAction | undefined,
  guide: AssistantGuideEntry
): AssistantLastAction | undefined {
  if (!action || !ASSISTANT_ACTION_RESULTS.has(action.result)) return undefined;
  const actionId = action.actionId as AssistantActionId;
  const valueId = action.valueId as AssistantValueId | undefined;
  if (!guide.actionIds.includes(action.actionId)) return undefined;
  if (valueId && (!guide.valueIds.includes(valueId) || !isAssistantActionValueCompatible(actionId, valueId))) {
    return undefined;
  }

  const itemId = action.itemId && guide.currentItemIds.includes(action.itemId)
    ? action.itemId
    : undefined;
  const evidenceId = action.evidenceId && guide.evidenceIds.includes(action.evidenceId)
    ? action.evidenceId
    : undefined;
  const misconceptionTag = action.misconceptionTag && MISCONCEPTION_TAGS.has(action.misconceptionTag)
    ? action.misconceptionTag
    : undefined;

  return {
    actionId: action.actionId,
    result: action.result,
    ...(valueId ? { valueId } : {}),
    ...(itemId ? { itemId } : {}),
    ...(evidenceId ? { evidenceId } : {}),
    ...(misconceptionTag ? { misconceptionTag } : {})
  };
}

function actionFromEvent(
  event: SceneProgress["events"][number] | undefined,
  guide: AssistantGuideEntry
): AssistantLastAction | undefined {
  if (!event) return undefined;
  return sanitizeAssistantAction({
    actionId: event.actionId,
    result: event.result,
    ...(event.valueId ? { valueId: event.valueId } : {}),
    ...(event.itemId ? { itemId: event.itemId } : {}),
    ...(event.evidenceId ? { evidenceId: event.evidenceId } : {}),
    ...(event.misconceptionTag ? { misconceptionTag: event.misconceptionTag } : {})
  }, guide);
}

interface CreateAssistantSnapshotOptions {
  sceneProgress: SceneProgress;
  lastAction?: AssistantLastAction;
  attemptCount?: number;
  consecutiveErrors?: number;
  currentItemId?: string;
  assistantControlValues?: SceneVisualState["controlValues"];
  misconceptionTags?: readonly MisconceptionTag[];
  recentMessages?: readonly AiDialogueMessage[];
}

/** 从本地规则状态生成完整快照；所有列表都会收窄到当前权威场景目录。 */
export function createAssistantContextSnapshot({
  sceneProgress,
  lastAction,
  attemptCount,
  consecutiveErrors,
  currentItemId,
  assistantControlValues,
  misconceptionTags,
  recentMessages = []
}: CreateAssistantSnapshotOptions): AssistantContextSnapshot {
  const guide = getAssistantGuide(sceneProgress.sceneId);
  const mastered = new Set(sceneProgress.masteredRuleIds);
  const masteredRuleIds = guide.ruleIds.filter((ruleId) => mastered.has(ruleId));
  const remainingRuleIds = guide.ruleIds.filter((ruleId) => !mastered.has(ruleId));
  const allowedEvidence = new Set(guide.evidenceIds);
  const viewedEvidenceIds = sceneProgress.evidenceViews.filter((evidenceId) => allowedEvidence.has(evidenceId));
  const latestEvent = sceneProgress.events.at(-1);
  const submissionEvents = sceneProgress.events.filter((event) => event.kind === "submit");
  const derivedConsecutiveErrors = [...submissionEvents].reverse()
    .findIndex((event) => event.result !== "error");
  const errorCount = derivedConsecutiveErrors === -1
    ? submissionEvents.length
    : derivedConsecutiveErrors;
  const boundedAttemptCount = Math.max(
    sceneProgress.attemptedRuleIds.length,
    Math.max(0, Math.floor(attemptCount ?? submissionEvents.length))
  );
  const resolvedConsecutiveErrors = Math.min(
    Math.max(0, consecutiveErrors ?? errorCount),
    boundedAttemptCount
  );
  const derivedTags = [...sceneProgress.events]
    .reverse()
    .flatMap((event) => event.result === "error" && event.misconceptionTag ? [event.misconceptionTag] : []);
  const boundedTags = [...new Set(misconceptionTags ?? derivedTags)]
    .filter((tag): tag is MisconceptionTag => MISCONCEPTION_TAGS.has(tag))
    .slice(0, 3);
  const defaultLastAction: AssistantLastAction = {
    actionId: "action.scene.enter",
    result: "pending",
    valueId: "value.none"
  };
  const resolvedLastAction = sanitizeAssistantAction(lastAction, guide)
    ?? actionFromEvent(latestEvent, guide)
    ?? defaultLastAction;
  const latestError = [...sceneProgress.events].reverse().find((event) => event.result === "error");
  const resolvedLastError = resolvedConsecutiveErrors > 0
    ? sanitizeAssistantAction(lastAction?.result === "error" ? lastAction : undefined, guide)
      ?? actionFromEvent(latestError, guide)
    : undefined;
  const latestStepSelection = sceneProgress.sceneId === "F01" || sceneProgress.sceneId === "F02"
    ? [...sceneProgress.events].reverse().find((event) =>
        event.kind === "select"
        && Boolean(event.itemId && guide.currentItemIds.includes(event.itemId)))
    : undefined;
  const resolvedCurrentItemId = [
    currentItemId,
    latestStepSelection?.itemId,
    resolvedLastAction.itemId,
    latestEvent?.evidenceId ? `item.${sceneProgress.sceneId.toLowerCase()}.evidence` : undefined,
    guide.currentItemIds[0]
  ].find((itemId): itemId is string => Boolean(itemId && guide.currentItemIds.includes(itemId)));
  const assessmentContextItemId = sceneProgress.sceneId === "F01" || sceneProgress.sceneId === "F02"
    ? resolvedCurrentItemId
    : undefined;
  const contextualizeAssessmentAction = (action: AssistantLastAction | undefined): AssistantLastAction | undefined => {
    if (!action || !assessmentContextItemId) return action;
    if (action.actionId !== "action.task.attempt" && action.actionId !== "action.task.retry") return action;
    return { ...action, itemId: assessmentContextItemId };
  };
  const snapshotLastAction = contextualizeAssessmentAction(resolvedLastAction) ?? resolvedLastAction;
  const snapshotLastError = contextualizeAssessmentAction(resolvedLastError);
  const activity = summarizeAssistantSceneActivity(sceneProgress.sceneId, assistantControlValues
    ? { ...sceneProgress.visualState, controlValues: assistantControlValues }
    : sceneProgress.visualState);
  const completedItemIds = [...new Set([...activity.completedItemIds, ...masteredRuleIds])];
  const completedItems = new Set(completedItemIds);
  const pendingItemIds = [...new Set([...activity.remainingItemIds, ...remainingRuleIds])]
    .filter((itemId) => !completedItems.has(itemId));
  const remainingItemIds = pendingItemIds.length === 0
    && sceneProgress.status !== "mastered"
    && resolvedCurrentItemId
    && !completedItems.has(resolvedCurrentItemId)
    ? [resolvedCurrentItemId]
    : pendingItemIds;

  return {
    sceneId: sceneProgress.sceneId,
    guideStepId: guide.guideStepId,
    phase: guide.phase,
    sceneStatus: sceneProgress.status,
    objectiveId: guide.objectiveId,
    lastAction: snapshotLastAction,
    attemptCount: boundedAttemptCount,
    consecutiveErrors: resolvedConsecutiveErrors,
    masteredRuleIds,
    remainingRuleIds,
    viewedEvidenceIds: [...new Set(viewedEvidenceIds)],
    ...(resolvedCurrentItemId ? { currentItemId: resolvedCurrentItemId } : {}),
    completedItemIds,
    remainingItemIds,
    controlValues: activity.controlValues,
    ...(snapshotLastError ? { lastError: snapshotLastError } : {}),
    misconceptionTags: boundedTags,
    recentMessages: boundedRecentMessages(recentMessages)
  };
}
